/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { graphql } from 'react-apollo';
import type { Link as ReactRouterLink } from 'react-router';
import Waypoint from 'react-waypoint';

import { compose, setDisplayName, withStateHandlers } from 'recompose';

import Retracked from 'js/lib/retracked';

import { Button, Grid, Link, PageGridContainer, Typography2 } from '@coursera/cds-core';
import type { ButtonProps, LinkProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { Heading, Section } from 'bundles/enterprise-legacy-learner-home/components/AutoHeading';
import { CollectionsView } from 'bundles/enterprise-legacy-learner-home/components/single-program/CuratedCollections';
import ForCreditCollection from 'bundles/enterprise-legacy-learner-home/components/single-program/ForCreditCollection';
import type {
  AcademicDisciplineNameQueryVariables,
  AcademicDisciplineSummariesResult,
} from 'bundles/enterprise-legacy-learner-home/components/single-program/SingleProgramGraphqlQueries';
import { AcademicDisciplineNameQuery } from 'bundles/enterprise-legacy-learner-home/components/single-program/SingleProgramGraphqlQueries';
import type { OnProductCardClick } from 'bundles/enterprise-legacy-learner-home/types/Products';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { IdTypes } from 'bundles/product-features';
import ProgramCollectionsView from 'bundles/program-home/components/enterprise-home/ProgramCollectionsView';
import SkillsFocus from 'bundles/program-home/components/enterprise-home/SkillsFocus';
import SkillSetCards from 'bundles/program-home/components/multiprogram/SkillSetCards';
import { withProductFeatures } from 'bundles/program-home/productFeatureFlags';
import type { EnterpriseProgramFieldsFragment as EnterpriseProgram } from 'bundles/program-home/queries/__generated__/EnterpriseHomePageQuery';

import _t from 'i18n!nls/program-home';

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps<ReactRouterLink>>(
  // TODO(ppaskaris): Ask how to type this.
  // @ts-expect-error
  Link
);

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps<ReactRouterLink>>(
  // TODO(ppaskaris): Ask how to type this.
  // @ts-expect-error
  Button
);

type PropsFromCaller = {
  enterpriseProgram: EnterpriseProgram;
  isAuthenticatedUser: boolean;
  onProductCardClick: OnProductCardClick;
  lazy?: boolean;
  isVisible: boolean;
  enableAcademicDiscipline?: boolean;
  allowOrgForCurriculumBuilder?: boolean;
  allowOrgForSpecializationConfiguration?: boolean;
  showSkillSetRoleFilter: boolean;
};

type PropsFromProductFeatures = { enableSkillsDashboard: boolean; hideCourseraRecommendations: boolean };

type PropsFromGraphql = {
  academicDisciplineName?: string;
};

type StateFromLazyLoad = {
  isLazyPlaceholder: boolean;
};

type StateHandlersFromLazyLoad = {
  lazyLoad: () => StateFromLazyLoad;
};

type Props = PropsFromCaller &
  StateFromLazyLoad &
  StateHandlersFromLazyLoad &
  PropsFromGraphql &
  PropsFromProductFeatures;

export function ProgramContainerRenderer({
  enterpriseProgram,
  isAuthenticatedUser,
  onProductCardClick,
  lazy,
  isVisible,
  lazyLoad,
  isLazyPlaceholder,
  academicDisciplineName,
  allowOrgForCurriculumBuilder,
  allowOrgForSpecializationConfiguration,
  showSkillSetRoleFilter,
  enableSkillsDashboard,
  hideCourseraRecommendations,
}: Props) {
  const hasTSPs = enterpriseProgram.metadata.linkedTargetSkillProfileIds.length > 0;
  if (!enterpriseProgram) {
    return null;
  }

  const { name: programName, slug: programSlug } = enterpriseProgram.metadata;
  const styles = {
    title: css`
      display: flex;
      flex-flow: row wrap;
      align-items: center;
      border-bottom: 1px solid var(--cds-color-grey-300);
      padding-bottom: var(--cds-spacing-150);
      margin-bottom: var(--cds-spacing-200);
    `,
    titleHeading: css`
      flex: 1 1 auto;
    `,
    titleLink: css`
      flex: 0 1 auto;
    `,
    description: css`
      margin-bottom: var(--cds-spacing-400);
      max-width: 712px;
    `,
    descriptionText: css`
      margin-right: var(--cds-spacing-100);
    `,
    helpButton: css`
      margin-left: calc(var(--cds-spacing-100) * -1);
    `,
    skillSets: css`
      margin-bottom: var(--cds-spacing-800);
    `,
    buttonWrapper: css`
      display: flex;
      justify-content: center;
      margin-top: var(--cds-spacing-600);
    `,
    gridContainer: css`
      padding: 0 !important; /* override PageGridContainer padding so content is aligned */
    `,
  };

  const skillSetsTitle = academicDisciplineName
    ? _t('SkillSets for the most popular job roles in #{academicDisciplineName}', { academicDisciplineName })
    : _t('SkillSets recommended by your organization');

  return (
    <div className="rc-ProgramContainer">
      <PageGridContainer css={styles.gridContainer}>
        <Grid item xs={12}>
          {lazy && isVisible ? <Waypoint onEnter={lazyLoad} /> : null}
          <div css={styles.title}>
            <Heading defaultLevel={2} css={styles.titleHeading}>
              {programName}
            </Heading>
            <TrackedLink
              {...Link.defaultProps}
              css={styles.titleLink}
              variant="quiet"
              typographyVariant="subtitleLarge"
              component={TrackedA}
              trackingName="explore_full_program_link"
              href={`/programs/${programSlug}`}
              aria-label={_t('Explore the full learning program: #{programName}', { programName })}
            >
              {_t('Explore the full learning program')}
            </TrackedLink>
          </div>
          <Section initialLevel={3}>
            <div css={styles.description}>
              <Typography2 component="p" css={styles.descriptionText}>
                {_t(
                  'Enroll in any course from this learning program. A learning program is a repository of content curated by your organization.'
                )}
              </Typography2>
            </div>
            {(!!academicDisciplineName || allowOrgForCurriculumBuilder) &&
              (isLazyPlaceholder ? (
                <CollectionsView.Placeholder length={1} />
              ) : (
                <ForCreditCollection
                  headingLevel={3}
                  programId={enterpriseProgram.id}
                  onProductCardClick={onProductCardClick}
                  allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
                />
              ))}
            {enableSkillsDashboard && (
              <SkillsFocus skillLimit={6} programSlug={programSlug} programId={enterpriseProgram.id} />
            )}

            {hasTSPs && (
              <div css={styles.skillSets}>
                {isLazyPlaceholder ? (
                  <SkillSetCards.Placeholder
                    programSlug={programSlug}
                    title={skillSetsTitle}
                    programName={enterpriseProgram.metadata.name}
                    renderOrgHomeVariant
                    skillSetLength={3}
                    showSkillSetRoleFilter={showSkillSetRoleFilter}
                  />
                ) : (
                  <SkillSetCards
                    programId={enterpriseProgram.id}
                    programSlug={programSlug}
                    title={skillSetsTitle}
                    programName={enterpriseProgram.metadata.name}
                    renderOrgHomeVariant
                    showCarousel
                    skillSetLength={3}
                    showSkillSetRoleFilter={showSkillSetRoleFilter}
                  />
                )}
              </div>
            )}
            {isLazyPlaceholder ? (
              <ProgramCollectionsView.Placeholder />
            ) : (
              <ProgramCollectionsView
                programId={enterpriseProgram.id}
                programSlug={enterpriseProgram.metadata.slug}
                programName={enterpriseProgram.metadata.name}
                isAuthenticatedUser={isAuthenticatedUser}
                onProductCardClick={onProductCardClick}
                academicDisciplineName={academicDisciplineName}
                allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
                enableCurriculumBuilder={allowOrgForCurriculumBuilder}
                hideCourseraRecommendations={hideCourseraRecommendations}
              />
            )}
            <div css={styles.buttonWrapper}>
              <TrackedButton
                {...Button.defaultProps}
                component={TrackedA}
                variant="primary"
                trackingName="explore_full_program_button"
                href={`/programs/${programSlug}`}
                aria-label={_t('Explore the full learning program: #{programName}', { programName })}
                icon={<ArrowNextIcon />}
                iconPosition="after"
              >
                {_t('Explore the full learning program')}
              </TrackedButton>
            </div>
            {lazy && isVisible ? <Waypoint onEnter={lazyLoad} /> : null}
          </Section>
        </Grid>
      </PageGridContainer>
    </div>
  );
}

const ProgramContainerHookProxy = (props: Props) => <ProgramContainerRenderer {...props} />;

const enhance = compose<Props, PropsFromCaller>(
  setDisplayName('ProgramContainer'),
  Retracked.createTrackedContainer<PropsFromCaller>(({ enterpriseProgram }) => ({ programId: enterpriseProgram.id })),
  withStateHandlers<StateFromLazyLoad, StateHandlersFromLazyLoad, PropsFromCaller>(
    ({ lazy }) => ({ isLazyPlaceholder: lazy === true }),
    {
      lazyLoad: () => () => ({ isLazyPlaceholder: false }),
    }
  ),
  withProductFeatures<PropsFromProductFeatures, PropsFromCaller>(
    ({ enterpriseProgram }) => ({
      idType: IdTypes.Program,
      id: enterpriseProgram.id,
    }),
    ({ features }) => ({
      enableSkillsDashboard: features.get('enterprise', 'enableSkillsDashboard'),
      hideCourseraRecommendations: features.get('enterprise', 'programHideCourseraRecommendations'),
    })
  ),
  graphql<PropsFromCaller, AcademicDisciplineSummariesResult, AcademicDisciplineNameQueryVariables, PropsFromGraphql>(
    AcademicDisciplineNameQuery,
    {
      skip: ({ enableAcademicDiscipline, enterpriseProgram }) =>
        !enableAcademicDiscipline || !enterpriseProgram?.metadata?.linkedAcademicDisciplineIds?.length,
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      options: ({ enterpriseProgram }) => {
        return {
          // Id is checked for in the skip above
          variables: { disciplineId: enterpriseProgram?.metadata.linkedAcademicDisciplineIds?.[0] },
          context: { clientName: 'gatewayGql' },
        };
      },
      props: ({ data }) => {
        const academicDisciplineName = data?.AcademicDiscipline?.queryAcademicDisciplineById.name;
        return { academicDisciplineName };
      },
    }
  )
);

export default enhance(ProgramContainerHookProxy);
